import React, { useState, useEffect } from 'react';
import { DatePicker, Button, Card, Switch, Tooltip } from 'antd';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import dayjs, { Dayjs } from 'dayjs';
import { AnalyticsService } from '../../../../services/analytics.service';
import { CampaignGraphEntryDTO, CampaignConversionGraphEntryDTO } from '../../../../dto/AnalyticDTO';

const { RangePicker } = DatePicker;

const CampaignDailyGraph: React.FC = () => {
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
        dayjs().subtract(7, 'day'),
        dayjs(),
    ]);
    const [dailyData, setDailyData] = useState<(CampaignGraphEntryDTO | CampaignConversionGraphEntryDTO)[]>([]);
    const [loading, setLoading] = useState(false);
    const [showConversionRates, setShowConversionRates] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        try {
            const startDate = dateRange[0].format('YYYY-MM-DD');
            const endDate = dateRange[1].format('YYYY-MM-DD');

            if (showConversionRates) {
                const response = await AnalyticsService.getCampaignConversionRatesAnalytics(startDate, endDate);
                setDailyData(response.data);
            } else {
                const response = await AnalyticsService.getCampaignGraphAnalytics(startDate, endDate);
                setDailyData(response.data);
            }
        } catch (error) {
            console.error('Error fetching campaign analytics', error);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, [dateRange, showConversionRates]);

    // Custom tooltip component that shows both raw counts and conversion rates
    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            const firstPayload = payload[0]?.payload;
            const hasConversionRates = firstPayload && 'installationRate' in firstPayload;

            return (
                <Card size="small" className="custom-tooltip" style={{ padding: '10px', border: '1px solid #d9d9d9' }}>
                    <p className="label" style={{ margin: 0, fontWeight: 'bold' }}>{`Date: ${label}`}</p>

                    {/* Always show the raw counts */}
                    <p style={{ margin: '5px 0', color: '#8884d8' }}>Generated: {firstPayload?.generated || 0}</p>
                    <p style={{ margin: '5px 0', color: '#82ca9d' }}>Installs: {firstPayload?.installs || 0}</p>
                    <p style={{ margin: '5px 0', color: '#ff7300' }}>Topups: {firstPayload?.topups || 0}</p>

                    {/* Show conversion rates if available in the data */}
                    {hasConversionRates && (
                        <>
                            <hr style={{ margin: '8px 0' }} />
                            <p style={{ margin: '5px 0', color: '#82ca9d' }}>Installation Rate: {firstPayload.installationRate}%</p>
                            <p style={{ margin: '5px 0', color: '#ff7300' }}>Topup Rate: {firstPayload.topupRate}%</p>
                        </>
                    )}
                </Card>
            );
        }
        return null;
    };

    return (
        <div style={{ marginBottom: '2rem' }}>
            <div className="flex items-center justify-between pb-4">
                <h3 className="font-semibold text-xl">Campaign Daily Analytics</h3>
                <div className="flex items-center space-x-2">
                    <span>Show Conversion Rates</span>
                    <Switch
                        checked={showConversionRates}
                        onChange={(checked) => setShowConversionRates(checked)}
                    />
                </div>
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
                <RangePicker
                    value={dateRange}
                    onChange={(dates) => {
                        if (dates) setDateRange(dates as [Dayjs, Dayjs]);
                    }}
                    allowClear={false}
                />
                <Tooltip title="Refresh Data">
                    <Button
                        type="primary"
                        onClick={fetchData}
                        style={{ marginLeft: '10px' }}
                    >
                        Refresh
                    </Button>
                </Tooltip>
            </div>

            {loading ? (
                <p>Loading campaign data...</p>
            ) : (
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={dailyData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis
                            domain={showConversionRates ? [0, 100] : undefined}
                            label={showConversionRates ? { value: 'Conversion %', angle: -90, position: 'insideLeft' } : undefined}
                        />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <Legend />

                        {showConversionRates && dailyData.length > 0 && 'installationRate' in dailyData[0] ? (
                            <>
                                <Line
                                    type="monotone"
                                    dataKey="installationRate"
                                    name="Installation Rate"
                                    stroke="#82ca9d"
                                    dot={true}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="topupRate"
                                    name="Topup Rate"
                                    stroke="#ff7300"
                                    dot={true}
                                />
                            </>
                        ) : (
                            <>
                                <Line
                                    type="monotone"
                                    dataKey="generated"
                                    name="Generated"
                                    stroke="#8884d8"
                                />
                                <Line
                                    type="monotone"
                                    dataKey="installs"
                                    name="Installs"
                                    stroke="#82ca9d"
                                />
                                <Line
                                    type="monotone"
                                    dataKey="topups"
                                    name="Topups"
                                    stroke="#ff7300"
                                />
                            </>
                        )}
                    </LineChart>
                </ResponsiveContainer>
            )}
        </div>
    );
};

export default CampaignDailyGraph;
